import { useEffect, useRef, useState, createContext, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, euro, apiError, fmtDate } from "@/lib/api";
import { Modal, Field, Select, PROVENIENZE, TextArea } from "@/pages/Notizie";
import { TipologiaSelect, CatastoSelect } from "@/lib/catalogo";
import { TempBadge, StatusBadge } from "@/components/common";
import { PhotoManager, DocumentManager } from "@/components/FileManager";
import GdprContactPanel from "@/components/GdprContactPanel";
import UnitoBanner from "@/components/UnitoBanner";
import { LongTextarea, NoteView } from "@/components/LongText";
import { AppuntamentiScheda } from "@/components/AppuntamentiScheda";
import { AzioniNotizia } from "@/components/NotiziaArchivio";
import ValuArt from "@/components/ValuArt";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ReassignModal } from "@/pages/Acquirenti";
import { GiraModal, SegnalataDa } from "@/components/GiraNotizia";
import { UnsavedBar, useUnsavedGuard, salvatoOk } from "@/components/UnsavedBar";
import { UltimoSalvataggio } from "@/components/SalvataggioUI";
import { ConvertiIncaricoModal } from "@/components/ConvertiIncarico";
import {
  ArrowLeft, Sparkles, Save, Check, Loader2, Plus, Phone, CalendarPlus, MapPin,
  User, Flame, Clock, FileText, Camera, History, Trash2, Lock,
} from "lucide-react";

const STAGES = [
  ["nuova", "Nuova"], ["primo_contatto", "Primo contatto"], ["follow_up", "Follow-up"],
  ["appuntamento", "Appuntamento"], ["sopralluogo", "Sopralluogo"], ["valutazione", "Valutazione"],
  ["trattativa", "Trattativa incarico"], ["incarico_acquisito", "Incarico acquisito"],
];
const SEGN_OK = ["nome", "cognome", "telefono", "email", "codice_fiscale", "indirizzo", "via_immobile", "zona", "comune", "tipologia", "categoria_catastale", "valore_stimato", "descrizione_immobile", "note", "note_generiche", "immobile_dati", "proprietario"];
const TABS = ["Panoramica", "Proprietario", "Immobile", "Catasto & Urbanistica", "Valutazione", "Documenti", "Foto", "Attività", "Storico"];
const CONDIZIONE = ["", "Nuovo", "Ottimo", "Buono", "Abitabile", "Da ristrutturare", "Da ristrutturare completamente"];
const SINO = [["", "—"], ["si", "Sì"], ["no", "No"]];
const CONF = [["da_verificare", "Da verificare"], ["conforme", "Conforme"], ["criticita", "Criticità rilevata"]];

function SubTitle({ children }) {
  return <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mt-6 mb-2 first:mt-0">{children}</h3>;
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }

// Finché l'indirizzo non è confermato, usiamo l'inizio del testo come titolo (non "Indirizzo da definire").
const testoStart = (t) => {
  const s = (t || "").trim();
  if (!s) return "";
  const words = s.split(/\s+/);
  return words.slice(0, 6).join(" ") + (words.length > 6 ? "…" : "");
};

// Inline field components are defined at MODULE scope (stable identity) so they are never
// remounted on re-render. Values/handlers flow through FormCtx. This fixes the bug where the
// input (e.g. Codice fiscale) lost focus on every keystroke.
const FormCtx = createContext(null);
const FKEY = (section, k) => `${section || "top"}:${k}`;

const useLock = (section, k) => {
  const { n } = useContext(FormCtx);
  if (!n._segnalatore || SEGN_OK.includes(section || k)) return null;
  return n.owner_agent_name || "chi ha ricevuto la notizia";
};
const Locked = ({ label, value, chi, testid, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""} data-testid={`${testid}-locked`}>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">{label}</label>
    <div className="w-full px-3 py-2.5 rounded-lg bg-slate-100 border border-slate-200 text-sm text-slate-500 flex items-center gap-2 cursor-not-allowed">
      <Lock size={13} className="shrink-0" /><span className="flex-1 truncate">{value === true ? "Sì" : value === false ? "No" : (value ?? "") === "" ? "—" : String(value)}</span>
      <span className="text-[10px] font-semibold whitespace-nowrap">Gestito da {chi}</span>
    </div>
  </div>
);
const T = ({ section, k, label, type = "text" }) => {
  const { n, setF, queue, fieldErrors } = useContext(FormCtx);
  const fk = FKEY(section, k);
  const chi = useLock(section, k);
  if (chi) return <Locked label={label} chi={chi} testid={`f-${section || "top"}-${k}`} value={(section ? (n[section] || {}) : n)[k]} />;
  return (
    <Field label={label} testid={`f-${section || "top"}-${k}`} type={type} error={fieldErrors[fk]}
      value={(section ? (n[section] || {}) : n)[k] ?? ""}
      onChange={(e) => { const val = type === "number" ? Number(e.target.value) : type === "euro" ? (e.target.value === "" ? null : e.target.value) : e.target.value; section ? setF(section, k, val) : queue({ [k]: val }, [fk]); }} />
  );
};
const ClassTop = () => {
  const { n, queue } = useContext(FormCtx);
  return (
    <>
      <TipologiaSelect value={n.tipologia || (n.immobile_dati || {}).tipologia} testid="f-top-tipologia" onChange={(v) => v && queue({ tipologia: v }, ["tipologia"])} />
      <CatastoSelect value={n.categoria_catastale} testid="f-top-catasto" onChange={(v) => queue({ categoria_catastale: v || null }, ["categoria_catastale"])} />
    </>
  );
};
const S = ({ section, k, label, opts }) => {
  const { n, setF, fieldErrors } = useContext(FormCtx);
  const chi = useLock(section, k);
  if (chi) { const v = (n[section] || {})[k]; const o = opts.find((x) => (Array.isArray(x) ? x[0] : x) === v); return <Locked label={label} chi={chi} testid={`f-${section}-${k}`} value={Array.isArray(o) ? o[1] : v} />; }
  return (
    <Select label={label} testid={`f-${section}-${k}`} error={fieldErrors[FKEY(section, k)]} value={(n[section] || {})[k] ?? (opts[0][0] ?? opts[0])} opts={opts}
      onChange={(e) => setF(section, k, e.target.value)} />
  );
};
const Area = ({ section, k, label }) => {
  const { n, setF, queue, fieldErrors } = useContext(FormCtx);
  const fk = FKEY(section, k);
  const err = fieldErrors[fk];
  const chi = useLock(section, k);
  if (chi) return <Locked wide label={label} chi={chi} testid={`f-${section || "top"}-${k}`} value={(section ? (n[section] || {}) : n)[k]} />;
  return (
    <div className="sm:col-span-2" data-field-error={err ? "true" : undefined}>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">{label}</label>
      <LongTextarea data-testid={`f-${section || "top"}-${k}`} rows={3}
        value={(section ? (n[section] || {}) : n)[k] ?? ""}
        onChange={(e) => section ? setF(section, k, e.target.value) : queue({ [k]: e.target.value }, [fk])}
        className={"w-full px-3 py-2.5 rounded-lg bg-white border outline-none text-sm transition-colors " + (err ? "border-red-500 focus:border-red-500 ring-1 ring-red-500/30" : "border-[var(--border)] focus:border-[#FC82ED]")} />
      {err && <p className="mt-1 text-xs text-red-600 font-medium">{err}</p>}
    </div>
  );
};
const Cb = ({ section, k, label }) => {
  const { n, setF } = useContext(FormCtx);
  const chi = useLock(section, k);
  if (chi) return <label className="flex items-center gap-2 text-sm py-1.5 text-slate-400 cursor-not-allowed" data-testid={`f-${section}-${k}-locked`}><Lock size={13} /> {label}: {(n[section] || {})[k] ? "Sì" : "No"} <span className="text-[10px] font-semibold">· Gestito da {chi}</span></label>;
  return (
    <label className="flex items-center gap-2 text-sm py-1.5 cursor-pointer">
      <input type="checkbox" data-testid={`f-${section}-${k}`} checked={!!(n[section] || {})[k]} onChange={(e) => setF(section, k, e.target.checked)} className="accent-[#FC82ED] w-4 h-4" /> {label}
    </label>
  );
};
const CUT = ({ i, k, label, type = "text" }) => {
  const { catastoUnits, setCatastoUnit, fieldErrors } = useContext(FormCtx);
  return (
    <Field label={label} testid={`f-catasto${i}-${k}`} type={type} error={fieldErrors[`catasto${i}:${k}`]}
      value={catastoUnits[i]?.[k] ?? ""}
      onChange={(e) => setCatastoUnit(i, k, type === "number" ? Number(e.target.value) : type === "euro" ? (e.target.value === "" ? null : e.target.value) : e.target.value)} />
  );
};
const CUS = ({ i, k, label, opts }) => {
  const { catastoUnits, setCatastoUnit, fieldErrors } = useContext(FormCtx);
  return (
    <Select label={label} testid={`f-catasto${i}-${k}`} error={fieldErrors[`catasto${i}:${k}`]} value={catastoUnits[i]?.[k] ?? (opts[0][0] ?? opts[0])} opts={opts}
      onChange={(e) => setCatastoUnit(i, k, e.target.value)} />
  );
};

export default function NotiziaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [n, setN] = useState(null);
  const { isDirezione, isSegreteria, user } = useAuth();
  const [agentsR, setAgentsR] = useState(null);
  const [gira, setGira] = useState(false);
  const [tab, setTab] = useState("Panoramica");
  const [saveState, setSaveState] = useState("saved"); // saved|dirty|saving
  const [appts, setAppts] = useState([]);
  const [confirmConv, setConfirmConv] = useState(false);
  const [callEsito, setCallEsito] = useState("fatta");
  const [evModal, setEvModal] = useState(false);
  const [apModal, setApModal] = useState(false);
  const dirty = useRef({});
  const dirtyPaths = useRef(new Set());
  const orig = useRef(null);
  const nRef = useRef(null);
  nRef.current = n;
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const refresh = (data) => { orig.current = data; setN({ ...data, ...dirty.current }); };

  const loadAppts = () => api.get("/appuntamenti").then((r) => setAppts((r.data || []).filter((a) => a.notizia_id === id))).catch(() => {});
  useEffect(() => {
    api.get(`/notizie/${id}`).then((r) => { orig.current = r.data; setN(r.data); }).catch(() => { toast.error("Notizia non trovata"); nav("/notizie"); });
    loadAppts();
  }, [id]);

  // Estrazione asincrona: finché è "in_corso" la scheda si aggiorna da sola (polling).
  useEffect(() => {
    if (n?.estrazione_stato !== "in_corso") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/notizie/${id}`);
        refresh(r.data);
        if (r.data?.estrazione_stato !== "in_corso") clearInterval(t);
      } catch { /* riprova al prossimo tick */ }
    }, 2000);
    return () => clearInterval(t);
  }, [n?.estrazione_stato, id]);

  const flush = async () => {
    const patch = dirty.current; const paths = Array.from(dirtyPaths.current);
    dirty.current = {}; dirtyPaths.current = new Set();
    if (!Object.keys(patch).length) { setSaveState("saved"); return true; }
    setSaveState("saving");
    try {
      await api.put(`/notizie/${id}`, patch);
      if (patch.valuart) {
        try { await api.post(`/valuart/notizia/${id}/salva`, { input: patch.valuart }); window.dispatchEvent(new CustomEvent("valuart-salvata", { detail: id })); } catch (e) { toast.warning(`Dati VALU-ART salvati, valutazione non registrata: ${apiError(e)}`); }
      }
      orig.current = { ...nRef.current, ...dirty.current };
      setSaveState(Object.keys(dirty.current).length ? "dirty" : "saved"); setSaveError(null); setFieldErrors({}); salvatoOk();
      return true;
    } catch (e) {
      // Il valore scritto NON va perso: il patch fallito resta in coda fino al prossimo "Salva".
      dirty.current = { ...patch, ...dirty.current };
      paths.forEach((pt) => dirtyPaths.current.add(pt));
      const fe = e?.response?.data?.detail?.field_errors || e?.response?.data?.field_errors;
      const next = {};
      if (fe && typeof fe === "object" && Object.keys(fe).length) {
        paths.forEach((pt) => { const leaf = pt.split(":").pop(); if (fe[leaf]) next[pt] = fe[leaf]; });
        if (!Object.keys(next).length) paths.forEach((pt) => { next[pt] = "Salvataggio non riuscito. Riprova."; });
      } else {
        paths.forEach((pt) => { next[pt] = "Salvataggio non riuscito. Il valore è stato mantenuto."; });
      }
      setFieldErrors((prev) => ({ ...prev, ...next }));
      setSaveState("dirty");
      setSaveError(`Salvataggio non riuscito: ${apiError(e)}`);
      return false;
    }
  };
  const annulla = () => {
    dirty.current = {}; dirtyPaths.current = new Set();
    setN(orig.current); setFieldErrors({}); setSaveError(null); setSaveState("saved");
  };
  const isDirty = saveState !== "saved";
  const { dialog: uscitaDialog } = useUnsavedGuard(isDirty, flush);
  const queue = (patch, paths = []) => {
    if (n._segnalatore) {
      const fuori = Object.keys(patch).filter((k) => !SEGN_OK.includes(k));
      if (fuori.length) { toast.info(`Questo dato lo gestisce ${n.owner_agent_name || "chi ha ricevuto la notizia"}.`, { id: "segn-campo" }); return; }
    }
    setN((p) => ({ ...p, ...patch }));
    dirty.current = { ...dirty.current, ...patch };
    paths.forEach((pt) => dirtyPaths.current.add(pt));
    if (paths.length) setFieldErrors((e) => { const n2 = { ...e }; let ch = false; paths.forEach((pt) => { if (n2[pt]) { delete n2[pt]; ch = true; } }); return ch ? n2 : e; });
    setSaveState("dirty");
  };
  const setF = (section, k, val) => queue({ [section]: { ...(n[section] || {}), [k]: val } }, [FKEY(section, k)]);

  const [contatto, setContatto] = useState(null);
  const changeStato = async (stato) => {
    const prevStato = n.stato;
    setN((p) => ({ ...p, stato }));
    try { await api.put(`/notizie/${id}`, { stato }); const r = await api.get(`/notizie/${id}`); refresh(r.data); }
    catch (e) {
      setN((p) => ({ ...p, stato: prevStato }));
      const msg = apiError(e);
      if (e.response?.status === 400 && msg.startsWith("Per passare")) { setContatto({ stato, nome: n.nome || n.proprietario?.nome || "", telefono: n.telefono || n.proprietario?.telefono || "" }); return; }
      toast.error(msg);
    }
  };
  const salvaContatto = async () => {
    if (!contatto.nome.trim() || !contatto.telefono.trim()) return toast.error("Inserisci nome e telefono");
    try {
      await api.put(`/notizie/${id}`, { nome: contatto.nome, telefono: contatto.telefono, proprietario: { ...(n.proprietario || {}), nome: contatto.nome, telefono: contatto.telefono }, stato: contatto.stato });
      const r = await api.get(`/notizie/${id}`); refresh(r.data); setContatto(null); toast.success("Dati salvati e stato aggiornato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const completeCall = async () => {
    try {
      await flush();
      await api.post(`/chiamate/notizia/${id}/completa`, { esito: callEsito });
      const r = await api.get(`/notizie/${id}`); refresh(r.data);
      toast.success("Chiamata segnata come completata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const applyProposta = async () => {
    const p = n.estrazione_proposta || {};
    const o = { estrazione_stato: "confermata" };
    if (p.proprietario) o.nome = p.proprietario;
    if (p.indirizzo) o.indirizzo = p.indirizzo;
    if (p.tipologia) o.tipologia = p.tipologia;
    if (p.tempistica) o.note_commerciali = p.tempistica;
    try { await api.put(`/notizie/${id}`, o); const r = await api.get(`/notizie/${id}`); refresh(r.data); toast.success("Campi applicati"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const dismissProposta = async () => {
    try { await api.put(`/notizie/${id}`, { estrazione_stato: "scartata" }); setN((p) => ({ ...p, estrazione_stato: "scartata" })); setTab("Immobile"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const riprovaEstrazione = async () => {
    try { await api.post(`/notizie/${id}/estrai`); setN((p) => ({ ...p, estrazione_stato: "in_corso", estrazione_motivo: null })); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!n) return <div className="space-y-4"><div className="skeleton h-24 rounded-2xl" /><div className="skeleton h-96 rounded-2xl" /></div>;

  const masked = n._masked;
  const prop = n.proprietario || {};
  const dati = n.immobile_dati || {};
  const val = n.valutazione || {};
  const lastEv = (n.timeline || []).slice(-1)[0];
  const stageIdx = STAGES.findIndex((s) => s[0] === n.stato);
  const converted = n.stato === "incarico_acquisito" && n.immobile_id;
  const coverUrl = n.cover_file_id ? `${API}/files/${n.cover_file_id}/download` : null;

  // Catasto multi-unità (retrocompatibile con il vecchio oggetto singolo `catasto`)
  const catastoUnits = Array.isArray(n.catasto_unita) && n.catasto_unita.length
    ? n.catasto_unita
    : (n.catasto && Object.keys(n.catasto || {}).length ? [n.catasto] : [{}]);
  const setCatastoUnit = (i, k, val) => queue({ catasto_unita: catastoUnits.map((u, idx) => idx === i ? { ...u, [k]: val } : u) }, [`catasto${i}:${k}`]);
  const addCatastoUnit = () => queue({ catasto_unita: [...catastoUnits, {}] });
  const removeCatastoUnit = (i) => queue({ catasto_unita: catastoUnits.filter((_, idx) => idx !== i) });

  return (
    <FormCtx.Provider value={{ n, setF, queue, catastoUnits, setCatastoUnit, fieldErrors }}>
    <div className="fade-up pb-10" data-testid="notizia-detail">
      <UnsavedBar dirty={isDirty} saving={saveState === "saving"} error={saveError} onSave={flush} onCancel={annulla} />
      {uscitaDialog}
      <div className="flex items-center justify-end gap-2 mb-4 flex-wrap">
        {!n._masked && !n._segnalatore && <AzioniNotizia notizia={n} onDone={() => nav("/notizie")} />}
      </div>
      <UnitoBanner tipo="notizia" rec={n} onRestored={() => window.location.reload()} />

      {/* HEADER */}
      <div className="card-immo overflow-hidden mb-4" data-testid="notizia-header">
        {coverUrl ? (
          <div className="relative h-48 md:h-56">
            <img src={coverUrl} alt="" data-testid="notizia-cover-hero" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute top-3 right-3 px-2 py-1 rounded-lg bg-black/40 backdrop-blur-sm">
              <SaveIndicator state={saveState} />
            </div>
            <div className="absolute bottom-4 left-5 right-5">
              <div className="flex items-center gap-2 mb-1">
                <TempBadge value={n.temperatura} />
                <StatusBadge value={n.stato} />
              </div>
              <h1 className="font-display text-2xl font-extrabold flex items-center gap-2 text-white">
                <MapPin size={20} className="text-[#FC82ED]" /> {dati.indirizzo || n.indirizzo || n.immobile || testoStart(n.testo_notizia) || "Indirizzo da definire"}
              </h1>
              <div className="text-sm text-slate-100 mt-1">{masked ? n.nome_display : `${n.nome || prop.nome || ""} ${n.cognome || prop.cognome || ""}`.trim() || "Proprietario da definire"} · Agente: <b className="text-white">{n.agente_referente || n.owner_agent_name || "Tu"}</b></div>
            </div>
          </div>
        ) : (
          <div className="p-5 pb-0">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <TempBadge value={n.temperatura} />
                  <StatusBadge value={n.stato} />
                  <SegnalataDa n={n} testid="notizia-segnalata-da" />
                </div>
                <h1 className="font-display text-2xl font-extrabold flex items-center gap-2"><MapPin size={20} className="text-[#FC82ED]" /> {dati.indirizzo || n.indirizzo || n.immobile || testoStart(n.testo_notizia) || "Indirizzo da definire"}</h1>
                <UltimoSalvataggio id={id} className="text-[var(--muted)] mt-0.5" />
                <div className="text-sm text-[var(--muted)] mt-1">{masked ? n.nome_display : `${n.nome || prop.nome || ""} ${n.cognome || prop.cognome || ""}`.trim() || "Proprietario da definire"} · Agente: <b className="text-slate-600">{n.agente_referente || n.owner_agent_name || "Tu"}</b>
                  {isDirezione && <button data-testid="notizia-reassign-btn" onClick={() => api.get("/agents?con_soci=true").then((r) => setAgentsR(r.data))} className="ml-2 text-xs font-semibold underline text-slate-600 hover:text-[#FC82ED]">Riassegna</button>}
                </div>
                {agentsR && <ReassignModal entityType="notizia" record={{ id }} agents={agentsR} onClose={() => setAgentsR(null)} onSaved={() => { setAgentsR(null); window.location.reload(); }} />}
                {!masked && (n.owner_agent_id === user?.id || isDirezione || isSegreteria) && !["incarico_acquisito", "persa"].includes(n.stato) && <button data-testid="notizia-gira-btn" onClick={() => setGira(true)} className="mt-1 text-xs font-semibold underline text-[#B8369F]">Gira a un'altra persona</button>}
                {n.note_girata && n.owner_agent_id === user?.id && <div data-testid="notizia-note-girata" className="mt-2 text-xs rounded-lg bg-[#FC82ED]/10 border-l-4 border-[#FC82ED] p-2"><b>Note da {n.girata_da_nome}:</b> {n.note_girata}</div>}
                {gira && <GiraModal id={id} onClose={() => setGira(false)} onDone={() => { setGira(false); window.location.reload(); }} />}
              </div>
              <div className="flex items-center gap-2">
                <SaveIndicator state={saveState} />
              </div>
            </div>
          </div>
        )}

        {n.testo_notizia && (
          <div className="mx-5 mt-4 p-4 rounded-xl border-2 border-[#FC82ED]/40 bg-[#FC82ED]/5" data-testid="notizia-testo">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#FC82ED] mb-1">Testo notizia</div>
            <p className="text-sm whitespace-pre-wrap break-words">{n.testo_notizia}</p>
          </div>
        )}

        {n.estrazione_stato === "in_corso" && (
          <div className="mx-5 mt-4 p-3 rounded-xl border border-[#FC82ED]/30 bg-[#FC82ED]/5 text-sm text-slate-600 flex items-center gap-2" data-testid="estrazione-incorso-notice">
            <Loader2 size={15} className="animate-spin text-[#FC82ED]" /> Sto leggendo il testo…
          </div>
        )}
        {n.estrazione_stato === "proposta" && n.estrazione_proposta && Object.keys(n.estrazione_proposta).length > 0 && (
          <div className="mx-5 mt-4 p-4 rounded-xl border-2 border-amber-300 bg-amber-50" data-testid="estrazione-proposta-banner">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1.5"><Sparkles size={13} /> Dal testo abbiamo capito</div>
            <div className="text-sm text-slate-700 mb-3" data-testid="estrazione-proposta-riepilogo">
              {[
                n.estrazione_proposta.proprietario && `Proprietario ${n.estrazione_proposta.proprietario}`,
                n.estrazione_proposta.indirizzo,
                n.estrazione_proposta.tipologia,
                n.estrazione_proposta.tempistica,
              ].filter(Boolean).join(" · ")}
            </div>
            <div className="flex gap-2">
              <button data-testid="estrazione-conferma" onClick={applyProposta} className="px-3.5 py-2 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold flex items-center gap-1.5"><Check size={15} /> Conferma</button>
              <button data-testid="estrazione-correggi" onClick={dismissProposta} className="px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-medium">Correggi</button>
            </div>
          </div>
        )}
        {n.estrazione_stato === "fallita" && (
          <div className="mx-5 mt-4 p-3 rounded-xl border border-amber-300 bg-amber-50 text-sm text-amber-800 flex items-center justify-between gap-3 flex-wrap" data-testid="estrazione-fallita-notice">
            <span>Compilazione automatica non riuscita, completa a mano.</span>
            <button data-testid="estrazione-riprova" onClick={riprovaEstrazione} className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 text-sm font-semibold">Riprova</button>
          </div>
        )}

        <div className="p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Info label="Valore stimato" v={euro(val.prezzo_consigliato || n.valore_stimato || 0)} />
          <Info label="Lead dal" v={fmtDate(n.created_at)} />
          <Info label="Ultima attività" v={lastEv ? `${lastEv.tipo === "riportato_a_notizia" ? "Riportato a notizia" : lastEv.tipo === "info_segnalatore" ? "Informazioni aggiunte dal segnalatore" : (lastEv.tipo || "").replace(/_/g, " ")} · ${fmtDate(lastEv.at)}` : "—"} />
          <Info label="Prossima attività" v={(n.prossima_attivita || {}).attivita || n.next_action || "—"} />
          <Info label="Fonte" v={n.fonte || "—"} />
        </div>

        {!masked && !n._segnalatore && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button data-testid="btn-modifica" onClick={() => setTab("Immobile")} className="px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-medium hover:border-[#FC82ED]/40">Modifica</button>
            <button data-testid="btn-add-attivita" onClick={() => setEvModal(true)} className="px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-medium hover:border-[#FC82ED]/40 flex items-center gap-1.5"><Plus size={15} /> Aggiungi attività</button>
            <button data-testid="btn-add-appuntamento" onClick={() => setApModal(true)} className="px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-medium hover:border-[#FC82ED]/40 flex items-center gap-1.5"><CalendarPlus size={15} /> Aggiungi appuntamento</button>
            {converted ? (
              <button data-testid="goto-incarico" onClick={() => nav(`/immobili/${n.immobile_id}`)} className="px-3.5 py-2 rounded-xl bg-emerald-500 text-white text-sm font-semibold flex items-center gap-1.5"><Check size={15} /> Vai alla scheda incarico</button>
            ) : (
              <button data-testid="btn-convert" onClick={() => setConfirmConv(true)} className="px-4 py-2 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black text-sm font-semibold glow-pink flex items-center gap-1.5"><Sparkles size={15} /> Trasforma notizia in incarico</button>
            )}
          </div>
        )}
        </div>
      </div>

      {masked ? (
        <div className="card-immo p-10 text-center text-[var(--muted)]"><User className="mx-auto mb-3" size={28} /> Dati riservati. Questa notizia appartiene a un altro agente.</div>
      ) : (
        <>
          {n._segnalatore && <div data-testid="notizia-banner-segnalatore" className="mb-4 p-3 rounded-xl bg-[#FC82ED]/10 border border-[#FC82ED]/40 text-sm">Hai segnalato questa notizia: puoi aggiungere informazioni (contatti, indirizzo, tipologia, valore, descrizione, note, allegati). Stato, temperatura, task e appuntamenti restano a <b>{n.owner_agent_name || "chi l'ha ricevuta"}</b>.</div>}
          {/* PROGRESS */}
          {!n._segnalatore && <div className="card-immo p-4 mb-4 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {STAGES.map((s, i) => (
                <div key={s[0]} className="flex items-center">
                  <button data-testid={`stage-${s[0]}`} onClick={() => changeStato(s[0])}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${i <= stageIdx ? "bg-[#FC82ED] text-black" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{s[1]}</button>
                  {i < STAGES.length - 1 && <div className={`w-5 h-0.5 ${i < stageIdx ? "bg-[#FC82ED]" : "bg-slate-200"}`} />}
                </div>
              ))}
              <div className="w-px h-5 bg-[var(--border)] mx-2" />
              <button data-testid="stage-richiamare" onClick={() => changeStato("richiamare")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${n.stato === "richiamare" ? "bg-amber-400 text-black" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Da richiamare</button>
              <button data-testid="stage-persa" onClick={() => changeStato("persa")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ml-1 ${n.stato === "persa" ? "bg-red-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Persa</button>
            </div>
          </div>}

          {/* TABS */}
          <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="detail-tabs">
            {TABS.filter((t) => !n._segnalatore || !["Attività", "Valutazione", "Catasto & Urbanistica"].includes(t)).map((t) => (
              <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${tab === t ? "bg-[#FC82ED] text-black font-semibold" : "text-slate-600 hover:bg-slate-100"}`}>{t}</button>
            ))}
          </div>

          <div className="card-immo p-5">
            {tab === "Panoramica" && (
              <>
                <SubTitle>Analisi acquisizione</SubTitle>
                <Grid>
                  <S section="analisi" k="temperatura_note" label="Temperatura" opts={[["", "—"], ["calda", "🔥 Calda"], ["media", "🟠 Media"], ["fredda", "❄️ Fredda"]]} />
                  <T section="analisi" k="urgenza" label="Urgenza" />
                  <Area section="analisi" k="motivo_vendita" label="Motivo vendita" />
                  <T section="analisi" k="tempistica" label="Tempistica prevista" />
                  <T section="analisi" k="aspettativa_economica" label="Aspettativa economica" />
                  <S section="analisi" k="esclusiva_possibile" label="Esclusiva possibile" opts={SINO} />
                  <T section="analisi" k="probabilita" label="Probabilità acquisizione %" type="number" />
                  <T section="analisi" k="gia_affidato" label="Già affidato ad altre agenzie" />
                  <Area section="analisi" k="criticita" label="Criticità" />
                  <Area section="analisi" k="opportunita" label="Opportunità" />
                </Grid>
                <SubTitle>Prossima attività · Chiamare entro il</SubTitle>
                <Grid>
                  <T k="chiamata_entro" label="Chiamare entro il" type="date" />
                  <div className="flex items-end pb-0.5">
                    {n.chiamata_completata ? (
                      <span data-testid="call-done-chip" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border text-emerald-600 bg-emerald-400/10 border-emerald-400/30"><Check size={14} /> Chiamata completata</span>
                    ) : n.chiamata_entro ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <select data-testid="detail-call-esito" value={callEsito} onChange={(e) => setCallEsito(e.target.value)}
                          className="px-2 py-2 rounded-lg bg-white border border-[var(--border)] text-xs outline-none focus:border-[#FC82ED]">
                          <option value="fatta">Fatta</option>
                          <option value="non_risponde">Non risponde</option>
                          <option value="da_rifare">Da rifare</option>
                        </select>
                        <button data-testid="detail-complete-call" onClick={completeCall}
                          className="px-3.5 py-2 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black text-sm font-semibold glow-pink flex items-center gap-1.5"><Phone size={14} /> Segna completata</button>
                      </div>
                    ) : null}
                  </div>
                  <T section="prossima_attivita" k="attivita" label="Attività" />
                  <T section="prossima_attivita" k="data" label="Data" type="date" />
                  <T section="prossima_attivita" k="ora" label="Ora" type="time" />
                  <T section="prossima_attivita" k="assegnato" label="Assegnato a" />
                  <S section="prossima_attivita" k="priorita" label="Priorità" opts={[["media", "Media"], ["alta", "Alta"], ["bassa", "Bassa"]]} />
                  <Area section="prossima_attivita" k="note" label="Note" />
                </Grid>
                <SubTitle>Immobile & note generiche</SubTitle>
                <Grid>
                  <T k="via_immobile" label="Via dell'immobile" />
                  {n._segnalatore ? <Locked label="Provenienza" chi={n.owner_agent_name || "chi ha ricevuto la notizia"} testid="f-top-provenienza" value={n.provenienza} /> : <Select label="Provenienza" testid="f-top-provenienza" value={n.provenienza ?? ""} error={fieldErrors["top:provenienza"]}
                    opts={[["", "— Seleziona —"], ...PROVENIENZE.map((p) => [p, p])]}
                    onChange={(e) => queue({ provenienza: e.target.value }, ["top:provenienza"])} />}
                  <Area k="descrizione_immobile" label="Descrizione immobile" />
                  <Area k="note_generiche" label="Note generiche" />
                </Grid>
                <SubTitle>Note</SubTitle>
                <Grid>
                  <Area k="note_commerciali" label="Note commerciali" />
                  <Area k="note_tecniche" label="Note tecniche" />
                  <Area k="note_interne" label="Note interne" />
                </Grid>
              </>
            )}

            {tab === "Proprietario" && (
              <Grid>
                <T section="proprietario" k="nome" label="Nome" />
                <T section="proprietario" k="cognome" label="Cognome" />
                <T section="proprietario" k="telefono" label="Telefono" />
                <T section="proprietario" k="telefono2" label="Telefono secondario" />
                <T section="proprietario" k="email" label="Email" />
                <T section="proprietario" k="codice_fiscale" label="Codice fiscale" />
                <T section="proprietario" k="data_nascita" label="Data di nascita" type="date" />
                <T section="proprietario" k="residenza" label="Indirizzo di residenza" />
                <T section="proprietario" k="comune" label="Comune" />
                <T section="proprietario" k="comproprietari" label="Altri comproprietari" />
                <T section="proprietario" k="percentuale" label="% di proprietà" type="number" />
                <T section="proprietario" k="relazione" label="Relazione comproprietari" />
                <T section="proprietario" k="disponibilita" label="Disponibilità a vendere" />
                <Area section="proprietario" k="motivazione" label="Motivazione vendita" />
                <T section="proprietario" k="tempistiche" label="Tempistiche desiderate" />
                <T section="proprietario" k="gia_sul_mercato" label="Già sul mercato" />
                <T section="proprietario" k="altra_agenzia" label="Altra agenzia coinvolta" />
                <Area section="proprietario" k="note_private" label="Note private" />
                <div className="sm:col-span-2"><GdprContactPanel contactType="notizia" contactId={id} /></div>
              </Grid>
            )}

            {tab === "Immobile" && (
              <>
                <SubTitle>Dati immobile</SubTitle>
                <Grid>
                  <T section="immobile_dati" k="indirizzo" label="Indirizzo completo" />
                  <T section="immobile_dati" k="civico" label="Numero civico" />
                  <T section="immobile_dati" k="interno" label="Interno" />
                  <T section="immobile_dati" k="cap" label="CAP" />
                  <T section="immobile_dati" k="comune" label="Comune" />
                  <T section="immobile_dati" k="provincia" label="Provincia" />
                  <T section="immobile_dati" k="zona" label="Quartiere / Zona" />
                  <ClassTop />
                  <T section="immobile_dati" k="destinazione" label="Destinazione d'uso" />
                  <T section="immobile_dati" k="piano" label="Piano" />
                  <T section="immobile_dati" k="piani_edificio" label="N° piani edificio" type="number" />
                  <S section="immobile_dati" k="ascensore" label="Ascensore" opts={SINO} />
                  <T section="immobile_dati" k="anno_costruzione" label="Anno costruzione" />
                  <T section="immobile_dati" k="anno_ristrutturazione" label="Anno ultima ristrutturazione" />
                  <S section="immobile_dati" k="stato" label="Stato immobile" opts={CONDIZIONE} />
                </Grid>
                <SubTitle>Superfici (m²)</SubTitle>
                <Grid>
                  {[["commerciale", "Commerciale"], ["calpestabile", "Calpestabile"], ["catastale", "Catastale"], ["balconi", "Balconi"], ["terrazzi", "Terrazzi"], ["giardino", "Giardino"], ["cantina", "Cantina"], ["garage", "Garage"], ["posto_auto", "Posto auto"], ["soffitta", "Soffitta"], ["taverna", "Taverna"], ["mansarda", "Mansarda"], ["altre_pertinenze", "Altre pertinenze"], ["terreno", "Terreno"]].map(([k, l]) => <T key={k} section="superfici" k={k} label={l} type="number" />)}
                </Grid>
                <SubTitle>Composizione</SubTitle>
                <Grid>
                  {[["locali", "N° locali"], ["soggiorno", "Soggiorno"], ["cucina", "Cucina"], ["camere_matrimoniali", "Camere matrimoniali"], ["camere_singole", "Camere singole"], ["bagni", "Bagni"], ["ripostigli", "Ripostigli"], ["lavanderia", "Lavanderia"], ["studio", "Studio"], ["ingresso", "Ingresso"], ["disimpegni", "Disimpegni"], ["cabine_armadio", "Cabine armadio"], ["altri_ambienti", "Altri ambienti"]].map(([k, l]) => <T key={k} section="composizione" k={k} label={l} />)}
                  <Area section="composizione" k="note_layout" label="Note sulla disposizione interna" />
                </Grid>
                <SubTitle>Caratteristiche</SubTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                  {[["balcone", "Balcone"], ["terrazzo", "Terrazzo"], ["giardino_privato", "Giardino privato"], ["giardino_condominiale", "Giardino condominiale"], ["garage", "Garage"], ["posto_auto", "Posto auto"], ["cantina", "Cantina"], ["ascensore", "Ascensore"], ["piscina", "Piscina"], ["camino", "Camino"], ["aria_condizionata", "Aria condizionata"], ["arredato", "Arredato"], ["porta_blindata", "Porta blindata"], ["videocitofono", "Videocitofono"], ["fibra", "Fibra"], ["accesso_disabili", "Accesso disabili"]].map(([k, l]) => <Cb key={k} section="caratteristiche" k={k} label={l} />)}
                </div>
                <Grid>
                  <T section="caratteristiche" k="esposizione" label="Esposizione" />
                  <T section="caratteristiche" k="vista" label="Vista" />
                  <T section="caratteristiche" k="luminosita" label="Luminosità" />
                  <T section="caratteristiche" k="silenziosita" label="Silenziosità" />
                </Grid>
                <SubTitle>Impianti</SubTitle>
                <Grid>
                  <S section="impianti" k="riscaldamento" label="Riscaldamento" opts={[["", "—"], ["autonomo", "Autonomo"], ["centralizzato", "Centralizzato"]]} />
                  <T section="impianti" k="tipologia_caldaia" label="Tipologia caldaia" />
                  <T section="impianti" k="alimentazione" label="Alimentazione" />
                  <T section="impianti" k="climatizzazione" label="Climatizzazione" />
                  <T section="impianti" k="impianto_elettrico" label="Impianto elettrico" />
                  <T section="impianti" k="impianto_idraulico" label="Impianto idraulico" />
                </Grid>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-2">
                  {[["riscaldamento_pavimento", "Riscaldamento a pavimento"], ["fotovoltaico", "Fotovoltaico"], ["pannelli_solari", "Pannelli solari"], ["accumulo", "Accumulo"], ["domotica", "Domotica"], ["conformita_docs", "Doc. conformità"]].map(([k, l]) => <Cb key={k} section="impianti" k={k} label={l} />)}
                </div>
                <SubTitle>Energia</SubTitle>
                <Grid>
                  <S section="energia" k="ape_disponibile" label="APE disponibile" opts={SINO} />
                  <T section="energia" k="classe" label="Classe energetica" />
                  <T section="energia" k="epgl" label="EPgl" type="number" />
                  <T section="energia" k="scadenza_ape" label="Scadenza APE" type="date" />
                  <T section="energia" k="codice_certificato" label="N° certificato" />
                  <Area section="energia" k="note" label="Note energia" />
                </Grid>
                <SubTitle>Condominio</SubTitle>
                <Grid>
                  <S section="condominio" k="condominio" label="Condominio" opts={SINO} />
                  <T section="condominio" k="amministratore" label="Amministratore" />
                  <T section="condominio" k="tel_amministratore" label="Telefono amministratore" />
                  <T section="condominio" k="email_amministratore" label="Email amministratore" />
                  <T section="condominio" k="spese_annuali" label="Spese annuali €" type="euro" />
                  <T section="condominio" k="spese_deliberate" label="Spese straord. deliberate" />
                  <T section="condominio" k="spese_previste" label="Spese straord. previste" />
                  <T section="condominio" k="lavori_recenti" label="Lavori recenti" />
                  <T section="condominio" k="fondo_cassa" label="Fondo cassa" />
                  <T section="condominio" k="morosita" label="Morosità" />
                  <Area section="condominio" k="note" label="Note condominio" />
                </Grid>
                <SubTitle>Stato di occupazione</SubTitle>
                <Grid>
                  <S section="occupazione" k="stato" label="Occupazione" opts={[["libero", "Libero"], ["proprietario", "Occupato dal proprietario"], ["locato", "Locato"], ["terzi", "Occupato da terzi"]]} />
                  <T section="occupazione" k="conduttore" label="Conduttore" />
                  <T section="occupazione" k="tipo_contratto" label="Tipologia contratto" />
                  <T section="occupazione" k="inizio" label="Data inizio" type="date" />
                  <T section="occupazione" k="scadenza" label="Data scadenza" type="date" />
                  <T section="occupazione" k="canone" label="Canone €" type="euro" />
                  <T section="occupazione" k="deposito" label="Deposito cauzionale €" type="euro" />
                  <Area section="occupazione" k="note" label="Note locazione" />
                </Grid>
              </>
            )}

            {tab === "Catasto & Urbanistica" && (
              <>
                <div className="flex items-center justify-between">
                  <SubTitle>Catasto (unità)</SubTitle>
                  <button data-testid="catasto-add-unit" onClick={addCatastoUnit}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FC82ED] hover:underline"><Plus size={14} /> Aggiungi unità</button>
                </div>
                {catastoUnits.map((u, i) => (
                  <div key={i} data-testid={`catasto-unit-${i}`} className="rounded-xl border border-[var(--border)] p-4 mb-3 bg-white/60">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)]">Unità {i + 1}</span>
                      {catastoUnits.length > 1 && (
                        <button data-testid={`catasto-remove-${i}`} onClick={() => removeCatastoUnit(i)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-red-500 hover:underline"><Trash2 size={13} /> Rimuovi</button>
                      )}
                    </div>
                    <Grid>
                      <CUT i={i} k="foglio" label="Foglio" />
                      <CUT i={i} k="particella" label="Particella / Mappale" />
                      <CUT i={i} k="subalterno" label="Subalterno" />
                      <CUT i={i} k="categoria" label="Categoria catastale" />
                      <CUT i={i} k="classe" label="Classe" />
                      <CUT i={i} k="consistenza" label="Consistenza" />
                      <CUT i={i} k="rendita" label="Rendita catastale €" type="euro" />
                      <CUT i={i} k="superficie" label="Superficie catastale" />
                      <CUT i={i} k="intestatari" label="Intestatari catastali" />
                      <CUS i={i} k="conformita" label="Conformità catastale" opts={CONF} />
                    </Grid>
                  </div>
                ))}
                <SubTitle>Urbanistica e conformità</SubTitle>
                <Grid>
                  <T section="urbanistica" k="titolo_edilizio" label="Titolo edilizio" />
                  <S section="urbanistica" k="conformita_urbanistica" label="Conformità urbanistica" opts={CONF} />
                  <S section="urbanistica" k="planimetria_conforme" label="Planimetria conforme" opts={CONF} />
                  <T section="urbanistica" k="difformita" label="Eventuali difformità" />
                  <T section="urbanistica" k="sanatorie" label="Sanatorie" />
                  <T section="urbanistica" k="condoni" label="Condoni" />
                  <T section="urbanistica" k="pratiche_aperte" label="Pratiche edilizie aperte" />
                  <T section="urbanistica" k="agibilita" label="Agibilità / Abitabilità" />
                  <T section="urbanistica" k="tecnico" label="Tecnico incaricato" />
                  <Area section="urbanistica" k="note" label="Note tecniche" />
                </Grid>
                <SubTitle>Situazione giuridica</SubTitle>
                <Grid>
                  <S section="giuridica" k="tipo_proprieta" label="Tipo proprietà" opts={[["piena", "Piena proprietà"], ["nuda", "Nuda proprietà"], ["usufrutto", "Usufrutto"], ["abitazione", "Diritto di abitazione"]]} />
                  <T section="giuridica" k="successione" label="Successione" />
                  <T section="giuridica" k="provenienza" label="Provenienza" />
                  <T section="giuridica" k="ipoteche" label="Ipoteche" />
                  <T section="giuridica" k="pignoramenti" label="Pignoramenti" />
                  <T section="giuridica" k="servitu" label="Servitù" />
                  <T section="giuridica" k="vincoli" label="Vincoli" />
                  <T section="giuridica" k="mutuo_residuo" label="Mutuo residuo €" type="euro" />
                  <T section="giuridica" k="banca" label="Banca" />
                  <Area section="giuridica" k="note" label="Note giuridiche" />
                </Grid>
              </>
            )}

            {tab === "Valutazione" && (
              masked ? <p className="text-sm text-[var(--muted)]">Dati riservati.</p> :
              <ValuArt tipo="notizia" id={id} value={n.valuart} onChange={(v) => queue({ valuart: v })} ro={!!n.immobile_id}
                onSaved={() => { delete dirty.current.valuart; if (!Object.keys(dirty.current).length) setSaveState("saved"); api.get(`/notizie/${id}`).then((r) => refresh(r.data)).catch(() => {}); }}
                indirizzo={dati.indirizzo ? [dati.indirizzo, dati.civico].filter(Boolean).join(" ") : (n.via_immobile || n.indirizzo)} comune={dati.comune || n.comune} mq={dati.mq || val.mq} tipologia={dati.tipologia || n.tipologia}>
                <div className="card-immo p-5" data-testid="valuart-extra"><Grid>
                  <Area section="valutazione" k="comparabili" label="Comparabili (facoltativo)" />
                  <Area section="valutazione" k="note" label="Note" />
                </Grid></div>
              </ValuArt>
            )}

            {tab === "Documenti" && (
              <DocumentManager entityType="notizia" entityId={id} readOnly={!!n.immobile_id} />
            )}

            {tab === "Foto" && (
              <PhotoManager entityType="notizia" entityId={id} readOnly={!!n.immobile_id} />
            )}

            {tab === "Attività" && (
              <AppuntamentiScheda entity={{ notizia_id: id }}
                prefill={{ property: { type: "notizia", id, label: dati.indirizzo || n.immobile || "Notizia" }, tipo: "appuntamento_acquisizione", titolo: "Appuntamento acquisizione" }}
                title="Appuntamenti & sopralluoghi" />
            )}

            {tab === "Storico" && (
              <>
                <SubTitle>Timeline attività</SubTitle>
                {(n.timeline || []).length === 0 ? <div className="text-sm text-[var(--muted)]">Nessun evento registrato.</div> : (
                  <div className="space-y-0">
                    {[...(n.timeline || [])].reverse().map((e, i) => (
                      <div key={i} className="flex gap-3 pb-4 relative">
                        <div className="flex flex-col items-center"><div className="w-2.5 h-2.5 rounded-full bg-[#FC82ED] mt-1.5" />{i < (n.timeline.length - 1) && <div className="w-px flex-1 bg-[var(--border)]" />}</div>
                        <div className="pb-2"><div className="text-sm font-medium capitalize">{(e.tipo || "").replace(/_/g, " ")}</div>{e.testo && <NoteView text={e.testo} className="text-xs text-slate-600" />}<div className="text-[11px] text-[var(--muted)] mt-0.5">{e.user || ""} · {fmtDate(e.at)}</div></div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {confirmConv && <ConvertiIncaricoModal n={n} prima={flush} onClose={() => setConfirmConv(false)} />}
      {contatto && (
        <Modal title="Dati del proprietario" onClose={() => setContatto(null)} onSave={salvaContatto} saveLabel="Salva e continua" testid="contatto-modal">
          <p className="text-sm text-slate-600">Per passare a questo stato servono nome e telefono del proprietario.</p>
          <Field label="Nome" testid="contatto-nome" value={contatto.nome} onChange={(e) => setContatto({ ...contatto, nome: e.target.value })} />
          <Field label="Telefono" testid="contatto-telefono" value={contatto.telefono} onChange={(e) => setContatto({ ...contatto, telefono: e.target.value })} />
        </Modal>
      )}
      {evModal && <EventoModal id={id} onClose={() => setEvModal(false)} onSaved={() => { setEvModal(false); api.get(`/notizie/${id}`).then((r) => setN(r.data)); }} />}
    </div>
    </FormCtx.Provider>
  );
}

function Info({ label, v }) {
  return <div><div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{label}</div><div className="font-medium truncate">{v}</div></div>;
}
function SaveIndicator({ state }) {
  if (state === "saving") return <span data-testid="save-state" className="text-xs text-[var(--muted)] flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Salvataggio…</span>;
  if (state === "dirty") return <span data-testid="save-state" className="text-xs text-amber-600 flex items-center gap-1.5"><Save size={13} /> Modifiche da salvare</span>;
  return <span data-testid="save-state" className="text-xs text-emerald-600 flex items-center gap-1.5"><Check size={13} /> Salvato</span>;
}

function ListEditor({ label, items, fields, onChange, icon: Icon, preview }) {
  const [draft, setDraft] = useState(Object.fromEntries(fields.map((f) => [f[0], ""])));
  const add = () => {
    if (!Object.values(draft).some((v) => v)) return;
    onChange([...(items || []), { ...draft, at: new Date().toISOString() }]);
    setDraft(Object.fromEntries(fields.map((f) => [f[0], ""])));
  };
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <SubTitle>{label}</SubTitle>
      <div className="grid sm:grid-cols-3 gap-2 items-end mb-3">
        {fields.map(([k, l]) => (
          <div key={k}><label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">{l}</label>
            <input data-testid={`add-${k}`} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" /></div>
        ))}
        <button data-testid="list-add-btn" onClick={add} className="px-3 py-2 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm flex items-center justify-center gap-1"><Plus size={15} /> Aggiungi</button>
      </div>
      {(!items || items.length === 0) ? <div className="text-sm text-[var(--muted)]">Nessun elemento caricato.</div> : (
        <div className={preview ? "grid grid-cols-2 sm:grid-cols-4 gap-3" : "space-y-2"}>
          {items.map((it, i) => preview ? (
            <div key={i} className="relative rounded-lg overflow-hidden border border-[var(--border)] group">
              {it.url ? <img src={it.url} alt={it.categoria} className="w-full h-28 object-cover" /> : <div className="w-full h-28 bg-slate-100 flex items-center justify-center"><Icon size={20} className="text-slate-400" /></div>}
              <div className="p-1.5 text-[11px] font-medium truncate">{it.categoria || "Foto"}</div>
              <button onClick={() => del(i)} className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-500 opacity-0 group-hover:opacity-100"><Trash2 size={13} /></button>
            </div>
          ) : (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border)]">
              <Icon size={16} className="text-[#FC82ED]" />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{it.nome || it.tipo || "Documento"}</div><div className="text-xs text-[var(--muted)] truncate">{it.tipo}{it.url ? ` · ${it.url}` : ""}</div></div>
              {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="text-xs text-[#FC82ED] font-semibold">Apri</a>}
              <button onClick={() => del(i)} className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventoModal({ id, onClose, onSaved }) {
  const [f, setF] = useState({ tipo: "nota", testo: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/notizie/${id}/evento`, f); toast.success("Attività registrata"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Aggiungi attività" onClose={onClose} onSave={save} saving={saving} testid="evento-modal">
      <Select label="Tipo" testid="ev-tipo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} opts={[["telefonata", "Telefonata"], ["nota", "Nota"], ["email", "Email"], ["whatsapp", "WhatsApp"], ["sopralluogo", "Sopralluogo"], ["valutazione", "Valutazione"], ["follow_up", "Follow-up"], ["cambio_temperatura", "Cambio temperatura"]]} />
      <TextArea label="Descrizione" testid="ev-testo" value={f.testo} onChange={(e) => setF({ ...f, testo: e.target.value })} />
    </Modal>
  );
}

function AppuntamentoModal({ id, titoloDefault, onClose, onSaved }) {
  const [f, setF] = useState({ titolo: titoloDefault || "Appuntamento acquisizione", tipo: "appuntamento_acquisizione", data: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.data) return toast.error("Scegli data e ora");
    setSaving(true);
    try {
      await api.post("/appuntamenti", { ...f, notizia_id: id });
      await api.post(`/notizie/${id}/evento`, { tipo: "appuntamento", testo: f.titolo });
      toast.success("Appuntamento creato e sincronizzato con l'Agenda");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Nuovo appuntamento" onClose={onClose} onSave={save} saving={saving} testid="appuntamento-modal">
      <Field label="Titolo" testid="ap-titolo" value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })} />
      <Select label="Tipo" testid="ap-tipo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} opts={[["appuntamento_acquisizione", "Appuntamento acquisizione"], ["sopralluogo", "Sopralluogo"], ["valutazione", "Valutazione"], ["visita", "Visita"]]} />
      <Field label="Data e ora" testid="ap-data" type="datetime-local" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} />
    </Modal>
  );
}
